<script setup>
import { onMounted, ref, computed } from "vue";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import Multiselect from "vue-multiselect";
import Datepicker from "vuejs3-datepicker";

const hotels = ref([]);
const hotelValue = ref([]);
const checkIn = ref(null);
const checkOut = ref(null);
const contracts = ref([]);
const loader = ref(false);

const formatShortDate = (dateStr) => {
  const date = new Date(dateStr);
  const month = date.getMonth() + 1; // JS months are 0-indexed
  const day = date.getDate();
  return `${day}/${month}`;
};

const getDayOfWeek = (dateStr) => {
  const daysOfWeek = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const date = new Date(dateStr);
  return daysOfWeek[date.getDay()];
};

const days = computed(() => contracts.value?.[0]?.days || []);

const formatHotelLabel = (hotel) => hotel.title;

const filterContracts = async () => {
  if (!checkIn.value || !checkOut.value) {
    Swal.fire({ text: "Please choose both dates", icon: "error" });
    return;
  }

  loader.value = true;
  try {
    const formData = new FormData();
    formData.append("from", moment(checkIn.value).format("YYYY-MM-DD"));
    formData.append("to", moment(checkOut.value).format("YYYY-MM-DD"));
    hotelValue.value.forEach((hotel) =>
      formData.append("hotel_ids[]", hotel.id)
    );

    const { data } = await axios.post(
      "/new_fetch_contract_report_upon_hotel_v2",
      formData
    );
    contracts.value = data.data || [];
  } catch (err) {
    Swal.fire({
      text: err.response?.data?.message || "Something went wrong",
      icon: "error",
    });
  } finally {
    loader.value = false;
  }
};

const searchHotel = async (event) => {
  const searchText = event?.target?.value || "";
  try {
    const { data } = await axios.post("/fetch_new_home_hotels", {
      word: searchText,
    });
    hotels.value = data.data.hotels || [];
  } catch (err) {
    console.error(err);
  }
};

onMounted(() => searchHotel());
</script>

<template>
  <div class="filter">
    <Multiselect
      v-model="hotelValue"
      :options="hotels"
      label="title"
      track-by="id"
      :multiple="true"
      :clear-on-select="true"
      :placeholder="'Choose hotel'"
      select-label=""
      :hide-selected="true"
      :custom-label="formatHotelLabel"
      @keyup="searchHotel"
    />
    <Datepicker v-model="checkIn" name="durationFrom" placeholder="From" />
    <Datepicker
      v-model="checkOut"
      name="durationTo"
      placeholder="To"
      :disabled-dates="{ to: checkIn }"
    />
    <button class="btn PrimaryButton m-0" @click="filterContracts">
      <i class="fa-solid fa-circle-notch fa-spin" v-if="loader"></i>
      <span v-else>Filter</span>
    </button>
  </div>

  <!-- Scrollable Table Section -->
  <div class="table-container" v-if="contracts.length">
    <div class="table-wrapper">
      <!-- Table Header -->
      <div class="header">
        <p class="header-cell sticky-col">ID</p>
        <p class="header-cell title sticky-col">Hotel</p>
        <div class="days">
          <div v-for="day in days" :key="day.date" class="day">
            <p class="date">{{ formatShortDate(day.date) }}</p>
            <span class="day-of-week">({{ getDayOfWeek(day.date) }})</span>
          </div>
        </div>
      </div>

      <!-- Table Rows -->
      <div
        v-for="(contract, index) in contracts"
        :key="contract?.hotel?.id || index"
        class="table-row"
      >
        <p class="cell sticky-col">{{ contract?.hotel?.id }}</p>
        <p class="cell title sticky-col">
          {{ contract?.hotel?.title.slice(0, 5) }}
        </p>
        <div class="numbers">
          <p
            v-for="day in contract?.days"
            :key="day.date"
            class="num-cell date"
          >
            {{ day?.allotment }}
          </p>
        </div>
      </div>
    </div>
  </div>

  <!-- No Data Section -->
  <div v-else class="no-data">
    <div class="d-flex align-items-center justify-content-center">
      <img
        src="@/assets/media/image/no-data.png"
        class="img-fluid"
        alt="No Data"
      />
    </div>
    <p class="text-center my-5 fs-3">{{ $t("Please Enter Data") }}</p>
  </div>
</template>

<style scoped>
.filter {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1.5rem;
}
.filter .multiselect,
.filter .vuejs3-datepicker {
  flex: 1;
  min-width: 200px;
}
.filter button.PrimaryButton {
  padding: 0.5rem 1.5rem;
  background-color: #1dd31d;
  color: #fff;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.3s;
}
.filter button.PrimaryButton:hover {
  background-color: #16b816;
  transform: translateY(-2px);
}
.filter button.PrimaryButton:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.filter i.fa-spin {
  margin-right: 0.5rem;
}

.table-container {
  overflow-x: auto;
  padding-bottom: 1rem;
}
.table-wrapper {
  display: table;
  border-collapse: collapse;
  width: 100%;
  min-width: 900px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.header {
  display: table-header-group;
  background-color: #f8f9fa;
  font-weight: 600;
  color: #333;
  position: sticky;
  top: 0;
  z-index: 10;
}
.date {
  border-right: 1px solid #e0e0e0;
  border-left: 1px solid #e0e0e0;
  padding: 0 1rem;
  margin: 0;
}
.header-cell {
  display: table-cell;
  text-align: center;
  padding: 0.75rem 1rem;
  border-bottom: 2px solid #e0e0e0;
  width: 100px;
}
.header-cell.title {
  width: 200px;
}
.days .day {
  display: table-cell;
  min-width: 100px;
  text-align: center;
  border-bottom: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
  border-left: 1px solid #e0e0e0;
}

.table-row {
  display: table-row;
  border-bottom: 1px solid #e0e0e0;
}
.table-row:nth-child(even) {
  background-color: #f9f9f9;
}
.table-row:hover {
  background-color: #e6f7e6;
}

.cell,
.num-cell {
  display: table-cell;
  text-align: center;
  padding: 0.5rem 1rem;
  white-space: nowrap;
  min-width: 100px;
}
.cell.title {
  width: 200px;
  text-align: start;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sticky-col {
  position: sticky;
  left: 0;
  background: #f8f9fa;
  border-right: 1px solid #e0e0e0;
  z-index: 5;
}

.no-data {
  text-align: center;
  padding: 2rem;
}

@media (max-width: 768px) {
  .filter {
    flex-direction: column;
    align-items: stretch;
  }
  .filter .multiselect,
  .filter .vuejs3-datepicker,
  .filter button.PrimaryButton {
    width: 100%;
  }
}
.day-of-week {
  font-size: small;
  font-weight: normal;
}
</style>
